import { useState, useEffect } from 'react';
import { Brain, Target, BarChart3, TrendingUp, HelpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export type QueryType = 'performance' | 'planning' | 'comparative' | 'predictive' | 'general';

export interface QueryAnalysis {
  type: QueryType;
  confidence: number;
  intent: string;
  entities: {
    players?: string[];
    timeRange?: string;
    metrics?: string[];
    skills?: string[];
  };
  suggestedQueries?: string[];
}

interface QueryUnderstandingProps {
  query: string;
  className?: string;
  onAnalysisUpdate?: (analysis: QueryAnalysis | null) => void;
}

const queryPatterns = {
  performance: {
    keywords: ['performance', 'stats', 'statistics', 'how is', 'how am', 'how was', 'batting', 'bowling', 'scoring', 'average', 'strike rate', 'improvement', 'progress'],
    icon: BarChart3,
    color: 'blue',
    examples: [
      "How is my batting performance this month?",
      "Show me Tom's bowling statistics",
      "What's my average strike rate?"
    ]
  },
  planning: {
    keywords: ['plan', 'training', 'focus', 'practice', 'should', 'next', 'tomorrow', 'session', 'drill', 'work on', 'improve', 'recommendation'],
    icon: Target,
    color: 'green',
    examples: [
      "What should I focus on in training?",
      "Plan a practice session for tomorrow",
      "What drills should Tom work on?"
    ]
  },
  comparative: {
    keywords: ['compare', 'vs', 'versus', 'better', 'worse', 'best', 'worst', 'top', 'bottom', 'rank', 'against', 'difference', 'between'],
    icon: TrendingUp,
    color: 'purple',
    examples: [
      "Compare my performance with Sarah",
      "Who are our top performers?",
      "How do I rank against my teammates?"
    ]
  },
  predictive: {
    keywords: ['predict', 'forecast', 'future', 'will', 'when', 'by when', 'target', 'goal', 'projection', 'expected', 'likely'],
    icon: Brain,
    color: 'orange',
    examples: [
      "When will I reach my target average?",
      "Predict my performance next month",
      "What's my expected improvement rate?"
    ]
  },
  general: {
    keywords: ['help', 'what', 'how', 'explain', 'tell me', 'information', 'about'],
    icon: HelpCircle,
    color: 'gray',
    examples: [
      "Help me understand my metrics",
      "Explain this technique",
      "Tell me about cricket scoring"
    ]
  }
};

export function QueryUnderstanding({ 
  query, 
  className, 
  onAnalysisUpdate 
}: QueryUnderstandingProps) {
  const [analysis, setAnalysis] = useState<QueryAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    if (query.trim().length < 3) {
      setAnalysis(null);
      onAnalysisUpdate?.(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      analyzeQuery(query);
    }, 300); // Debounce

    return () => clearTimeout(timeoutId);
  }, [query]);

  const analyzeQuery = async (queryText: string) => {
    setIsAnalyzing(true);
    
    try {
      // Real-time query analysis using pattern matching and NLP
      const queryAnalysis = performLocalAnalysis(queryText);
      
      // In a production app, you might also send to LLM service for enhanced analysis
      // const enhancedAnalysis = await enhanceWithLLM(queryAnalysis);
      
      setAnalysis(queryAnalysis);
      onAnalysisUpdate?.(queryAnalysis);
    } catch (error) {
      console.error('Query analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const performLocalAnalysis = (queryText: string): QueryAnalysis => {
    const lowercaseQuery = queryText.toLowerCase();
    const words = lowercaseQuery.split(/\s+/);
    
    // Calculate confidence scores for each query type
    const scores: Record<QueryType, number> = {
      performance: 0,
      planning: 0,
      comparative: 0,
      predictive: 0,
      general: 0
    };

    // Score based on keyword matches
    Object.entries(queryPatterns).forEach(([type, pattern]) => {
      const keywordMatches = pattern.keywords.filter(keyword => 
        lowercaseQuery.includes(keyword)
      ).length;
      
      scores[type as QueryType] = keywordMatches / pattern.keywords.length;
    });

    // Boost scores based on specific patterns
    if (lowercaseQuery.includes('compare') || lowercaseQuery.includes(' vs ')) {
      scores.comparative += 0.3;
    }
    
    if (lowercaseQuery.includes('will') || lowercaseQuery.includes('predict')) {
      scores.predictive += 0.3;
    }
    
    if (lowercaseQuery.includes('should') || lowercaseQuery.includes('focus')) {
      scores.planning += 0.3;
    }
    
    if (lowercaseQuery.includes('performance') || lowercaseQuery.includes('stats')) {
      scores.performance += 0.3;
    }

    // Determine primary type and confidence
    const [primaryType, confidence] = Object.entries(scores).reduce(
      ([maxType, maxScore], [type, score]) => 
        score > maxScore ? [type as QueryType, score] : [maxType, maxScore],
      ['general' as QueryType, 0]
    );

    // Extract entities
    const entities = extractEntities(queryText, words);
    
    // Generate intent description
    const intent = generateIntent(primaryType as QueryType, entities);

    // Generate suggested queries
    const suggestedQueries = generateSuggestedQueries(primaryType as QueryType, entities);

    return {
      type: primaryType as QueryType,
      confidence: Math.min(confidence, 1.0),
      intent,
      entities,
      suggestedQueries
    };
  };

  const extractEntities = (queryText: string, _words: string[]) => {
    const entities: QueryAnalysis['entities'] = {};
    
    // Extract player names (simple pattern matching)
    const playerPatterns = /\b[A-Z][a-z]+\b/g;
    const potentialNames = queryText.match(playerPatterns) || [];
    if (potentialNames.length > 0) {
      entities.players = potentialNames.slice(0, 3); // Limit to 3 names
    }
    
    // Extract time ranges
    const timePatterns = [
      'today', 'yesterday', 'this week', 'last week', 'this month', 'last month',
      'this year', 'last year', 'past 7 days', 'past 30 days', 'past 3 months'
    ];
    
    const timeRange = timePatterns.find(pattern => 
      queryText.toLowerCase().includes(pattern)
    );
    if (timeRange) {
      entities.timeRange = timeRange;
    }
    
    // Extract cricket metrics
    const metricPatterns = [
      'average', 'strike rate', 'runs', 'wickets', 'catches', 'boundaries',
      'sixes', 'fours', 'dot balls', 'economy rate', 'accuracy', 'timing'
    ];
    
    entities.metrics = metricPatterns.filter(metric => 
      queryText.toLowerCase().includes(metric)
    );
    
    // Extract skills/techniques
    const skillPatterns = [
      'batting', 'bowling', 'fielding', 'footwork', 'timing', 'power',
      'placement', 'technique', 'stance', 'swing', 'follow through'
    ];
    
    entities.skills = skillPatterns.filter(skill => 
      queryText.toLowerCase().includes(skill)
    );
    
    return entities;
  };

  const generateIntent = (type: QueryType, entities: QueryAnalysis['entities']): string => {
    const baseIntents = {
      performance: 'Analyze cricket performance metrics and statistics',
      planning: 'Generate training plans and coaching recommendations',
      comparative: 'Compare performance between players or time periods',
      predictive: 'Forecast future performance and improvement projections',
      general: 'Provide general cricket information and guidance'
    };
    
    let intent = baseIntents[type];
    
    // Enhance intent with specific entities
    if (entities.players?.length) {
      intent += ` for ${entities.players.join(', ')}`;
    }
    
    if (entities.timeRange) {
      intent += ` over ${entities.timeRange}`;
    }
    
    if (entities.metrics?.length) {
      intent += ` focusing on ${entities.metrics.join(', ')}`;
    }
    
    return intent;
  };

  const generateSuggestedQueries = (type: QueryType, entities: QueryAnalysis['entities']): string[] => {
    const baseQueries = queryPatterns[type].examples;
    
    // Customize suggestions based on entities
    const suggestions = [...baseQueries];
    
    if (entities.players?.length) {
      const player = entities.players[0];
      suggestions.push(`Show ${player}'s latest performance trends`);
    }
    
    if (entities.timeRange) {
      suggestions.push(`Compare ${entities.timeRange} with previous period`);
    }
    
    return suggestions.slice(0, 3);
  };

  if (!query.trim() || query.length < 3) {
    return null;
  }

  return (
    <Card className={cn("p-3 bg-gray-50/50 border-dashed", className)}>
      <div className="flex items-center gap-2 mb-2">
        {isAnalyzing ? (
          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
        ) : analysis ? (
          (() => {
            const pattern = queryPatterns[analysis.type];
            const IconComponent = pattern.icon;
            return <IconComponent className="h-4 w-4 text-gray-600" />;
          })()
        ) : null}
        
        <span className="text-sm font-medium text-gray-700">
          {isAnalyzing ? 'Analyzing query...' : 'Query Understanding'}
        </span>
      </div>
      
      {analysis && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge 
              variant="outline"
              className={cn(
                "capitalize",
                analysis.type === 'performance' && "border-blue-200 text-blue-700",
                analysis.type === 'planning' && "border-green-200 text-green-700",
                analysis.type === 'comparative' && "border-purple-200 text-purple-700",
                analysis.type === 'predictive' && "border-orange-200 text-orange-700",
                analysis.type === 'general' && "border-gray-200 text-gray-700"
              )}
            >
              {analysis.type}
            </Badge>
            
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <span>Confidence:</span>
              <div className="w-12 h-1 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${analysis.confidence * 100}%` }}
                />
              </div>
              <span>{Math.round(analysis.confidence * 100)}%</span>
            </div>
          </div>
          
          <p className="text-xs text-gray-600">{analysis.intent}</p>
          
          {/* Entities */}
          {(analysis.entities.players?.length || analysis.entities.metrics?.length || analysis.entities.timeRange) && (
            <div className="flex flex-wrap gap-1">
              {analysis.entities.players?.map(player => (
                <Badge key={player} variant="secondary" className="text-xs">
                  👤 {player}
                </Badge>
              ))}
              
              {analysis.entities.timeRange && (
                <Badge variant="secondary" className="text-xs">
                  📅 {analysis.entities.timeRange}
                </Badge>
              )}
              
              {analysis.entities.metrics?.map(metric => (
                <Badge key={metric} variant="secondary" className="text-xs">
                  📊 {metric}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}